import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CurrencyOption } from './currency.catalog';
import { currencyMatchesQuery } from './currency.utils';

@Component({
  selector: 'app-currency-picker-panel',
  templateUrl: './currency-picker-panel.html',
})
export class CurrencyPickerPanel implements AfterViewInit {
  readonly currencies = input<readonly CurrencyOption[]>([]);
  readonly selectedCurrency = input<CurrencyOption | null>(null);
  readonly iconSize = input(20);
  readonly panelWidth = input<number | null>(null);
  readonly listboxLabelledBy = input<string>();
  readonly panelId = input<string>();
  readonly noOptionsMessage = input('No currencies found');

  readonly currencySelected = output<CurrencyOption>();
  readonly dismissed = output<void>();

  readonly searchQuery = signal('');
  readonly highlightedIndex = signal(-1);

  private readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField');

  readonly filteredCurrencies = computed(() => {
    const query = this.searchQuery();
    return this.currencies().filter((currency) => currencyMatchesQuery(currency, query));
  });

  readonly activeOptionId = computed(() => {
    const currency = this.filteredCurrencies()[this.highlightedIndex()];
    return currency ? this.optionId(currency.code) : null;
  });

  ngAfterViewInit(): void {
    queueMicrotask(() => this.searchField()?.nativeElement.focus());
    this.updateHighlightedIndex();
  }

  optionId(code: string): string {
    return `${this.panelId()}-${code}`;
  }

  isCurrencySelected(currency: CurrencyOption): boolean {
    return this.selectedCurrency()?.code === currency.code;
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.updateHighlightedIndex();
  }

  onClearSearch(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchQuery.set('');

    const field = this.searchField()?.nativeElement;
    if (field) {
      field.value = '';
      field.focus();
    }

    this.updateHighlightedIndex();
  }

  onPanelKeyDown(event: KeyboardEvent): void {
    const list = this.filteredCurrencies();
    let handled = false;

    switch (event.key) {
      case 'ArrowDown':
        if (this.highlightedIndex() < list.length - 1) {
          this.highlightedIndex.update((index) => index + 1);
        }
        handled = true;
        break;
      case 'ArrowUp':
        if (this.highlightedIndex() > 0) {
          this.highlightedIndex.update((index) => index - 1);
        }
        handled = true;
        break;
      case 'Home':
        if (list.length > 0) {
          this.highlightedIndex.set(0);
        }
        handled = true;
        break;
      case 'End':
        if (list.length > 0) {
          this.highlightedIndex.set(list.length - 1);
        }
        handled = true;
        break;
      case 'Enter': {
        const currency = list[this.highlightedIndex()];
        if (currency) {
          this.currencySelected.emit(currency);
        }
        handled = true;
        break;
      }
      case 'Escape':
        this.dismissed.emit();
        handled = true;
        break;
      default:
        break;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private updateHighlightedIndex(): void {
    const list = this.filteredCurrencies();
    const selected = this.selectedCurrency();
    const index = list.findIndex((currency) => currency.code === selected?.code);
    this.highlightedIndex.set(index >= 0 ? index : list.length > 0 ? 0 : -1);
  }
}
